import { useEffect } from "react";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { installAuthRuntime } from "./auth/runtime";
import { AuthRequired } from "./components/AuthRequired";
import { Layout } from "./components/Layout";
import { Login } from "./pages/Login";
import { SessionStatus } from "./pages/SessionStatus";
import { Devices } from "./pages/Devices";
import { Approvals } from "./pages/Approvals";
import { ApprovalDetail } from "./pages/ApprovalDetail";
import { ApprovalsHistory } from "./pages/ApprovalsHistory";
import { SettlementControl } from "./pages/SettlementControl";
import { EscrowBuckets } from "./pages/EscrowBuckets";
import { MoneyOperations } from "./pages/MoneyOperations";
import { MoneyDashboard } from "./pages/MoneyDashboard";
import { PartnerPayables } from "./pages/PartnerPayables";
import { PartnerPayableDetail } from "./pages/PartnerPayableDetail";
import { TreasurySummaryPage } from "./pages/TreasurySummaryPage";
import { WithdrawAffiliate } from "./pages/WithdrawAffiliate";
import { WithdrawTreasury } from "./pages/WithdrawTreasury";
import { RefundCandidates } from "./pages/RefundCandidates";
import { RefundDetail } from "./pages/RefundDetail";
import { AffiliateWithdrawals } from "./pages/AffiliateWithdrawals";
import { AffiliateWithdrawalDetail } from "./pages/AffiliateWithdrawalDetail";
import { AffiliateRedemptions } from "./pages/AffiliateRedemptions";
import { AffiliateRedemptionDetail } from "./pages/AffiliateRedemptionDetail";
import { PartnerApplications } from "./pages/PartnerApplications";
import { PartnerApplicationDetail } from "./pages/PartnerApplicationDetail";
import { ControlPlaneOverview } from "./pages/ControlPlaneOverview";
import { ControlPlaneProduct } from "./pages/ControlPlaneProduct";
import { ControlPlaneEditRoute } from "./pages/LocalAIModelsEditor";
import { ControlPlaneReview } from "./pages/ControlPlaneReview";
import { ControlPlaneAudit } from "./pages/ControlPlaneAudit";
import { ControlPlaneAuthorityGate } from "./controlPlane/ControlPlaneAuthorityGate";

export function App() {
  // Subscribe to the wallet. Before Phase 1 this console listened to nothing:
  // `accountsChanged` and `chainChanged` appeared nowhere in the source, so an
  // operator could switch to a non-owner account, or to another network, and
  // the header would go on presenting the previous signer's authority
  // indefinitely. See `src/auth/provider-events.ts` for what each event means.
  useEffect(() => installAuthRuntime(), []);

  return (
    <BrowserRouter>
      {/* The Control Plane's own write authority, read from the server on
          every navigation. It wraps the whole tree because the surfaces that
          consume it (the Control Plane screens and the approval detail for a
          Control Plane proposal) are spread across the route table — but it
          grants nothing to any other domain: only `useControlPlaneWritesBlocked`
          reads this context, and the money surfaces read the separate,
          provider-less gate in `src/authority.tsx`, which stays closed. */}
      <ControlPlaneAuthorityGate>
        <Routes>
          <Route element={<Layout />}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/session" element={<SessionStatus />} />
            <Route path="/devices" element={<Devices />} />
            <Route path="/approvals" element={<Approvals />} />
            <Route path="/approvals/history" element={<ApprovalsHistory />} />
            <Route path="/approvals/:id" element={<ApprovalDetail />} />
            <Route path="/settlement-control" element={<SettlementControl />} />
            {/* Product Control Plane — the product DATA Gao apps read at runtime.
              Not this platform's deployment control plane; the two share a
              phrase and nothing else.

              GUARDED AT THE ROUTE, not inside each page body. All five need a
              session and none had one: an operator without a session got the
              chrome, then five 401s, then a page of error text — instead of
              the "Login and return here" affordance every other privileged
              screen in this console gives them. One place to add a route to,
              rather than five component bodies to remember. */}
            <Route
              path="/control-plane"
              element={
                <AuthRequired>
                  <ControlPlaneOverview />
                </AuthRequired>
              }
            />
            <Route
              path="/control-plane/:productId"
              element={
                <AuthRequired>
                  <ControlPlaneProduct />
                </AuthRequired>
              }
            />
            <Route
              path="/control-plane/:productId/edit"
              element={
                <AuthRequired>
                  <ControlPlaneEditRoute />
                </AuthRequired>
              }
            />
            <Route
              path="/control-plane/:productId/review"
              element={
                <AuthRequired>
                  <ControlPlaneReview />
                </AuthRequired>
              }
            />
            <Route
              path="/control-plane/:productId/audit"
              element={
                <AuthRequired>
                  <ControlPlaneAudit />
                </AuthRequired>
              }
            />
            <Route path="/dashboard" element={<MoneyDashboard />} />
            <Route path="/treasury" element={<TreasurySummaryPage />} />
            <Route
              path="/admin/partner-payables"
              element={<PartnerPayables />}
            />
            <Route
              path="/admin/partner-payables/:id"
              element={<PartnerPayableDetail />}
            />
            <Route path="/money" element={<MoneyOperations />} />
            <Route path="/escrow/buckets" element={<EscrowBuckets />} />
            <Route
              path="/escrow/withdraw-affiliate"
              element={<WithdrawAffiliate />}
            />
            <Route
              path="/escrow/withdraw-treasury"
              element={<WithdrawTreasury />}
            />
            <Route path="/escrow/refunds" element={<RefundCandidates />} />
            <Route
              path="/escrow/refunds/:paymentIntentId"
              element={<RefundDetail />}
            />
            <Route
              path="/admin/affiliate/withdrawals"
              element={<AffiliateWithdrawals />}
            />
            <Route
              path="/admin/affiliate/withdrawals/:id"
              element={<AffiliateWithdrawalDetail />}
            />
            <Route
              path="/admin/affiliate/redemptions"
              element={<AffiliateRedemptions />}
            />
            <Route
              path="/admin/affiliate/redemptions/:id"
              element={<AffiliateRedemptionDetail />}
            />
            <Route
              path="/admin/affiliate/partners"
              element={<PartnerApplications />}
            />
            <Route
              path="/admin/affiliate/partners/:id"
              element={<PartnerApplicationDetail />}
            />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Route>
        </Routes>
      </ControlPlaneAuthorityGate>
    </BrowserRouter>
  );
}
